import { Store } from '@ngrx/store';
import { IController } from 'angular';
import { cloneDeep, find, size } from 'lodash';
import { SdcActionsService } from 'src/app/sdc/flux/sdc-actions.service';
import { LoadUsersSuccess } from './../../../../../ng2/store/actions/users-actions';

import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import {
  JobRolesOptions,
  UserAccessLevelToRoleTypeNoSchoolAdminMapping,
} from '../../../../../ng2/shared/constants/manage-users.constant';
import {
  INewSchoolUserTemplate,
  INycDoeCentralUserSourceMetaData,
  ISchoolUserSourceMetaData,
} from '../../../../../ng2/shared/typings/interfaces/user.interface';
import { IManageUsersSchool } from './../../../../features/manage-users/manage-users.controller';
import {
  IModalUserCreateOrUpdate,
  TValidModalUserCreateOrUpdateModes,
} from './manage-users-create-or-edit-user-modal.service';

// this is the form in the html file for this modal
// update this interface when making changes to createOrUpdateUserForm in the html
interface ICreateOrUpdateUserFormProp {
  $error: {
    required?: boolean;
    pattern?: boolean;
    conflict?: boolean;
  };
  $touched: boolean;
}

interface ICreateOrUpdateUserForm {
  firstName: ICreateOrUpdateUserFormProp;
  lastName: ICreateOrUpdateUserFormProp;
  googleEmail: ICreateOrUpdateUserFormProp;
  doeEmail: ICreateOrUpdateUserFormProp;
  delegatedRole: ICreateOrUpdateUserFormProp;
  $error: any;
}

export class ManageUsersCreateNewUserModalCtrl implements IController {
  static $inject: string[] = [
    '$mdDialog',
    'ErrorModal',
    'school',
    'modalUser',
    'schoolUsers',
    'MODE',
    'User',
    'UtilitiesService',
    'Rollbar',
    'Store',
    'sdcActions',
  ];

  // bindings

  // class properties
  newUser;
  delegatedRole;
  JobRolesOptions;
  doeEmployee: boolean;
  createDoeEmailSelected: boolean;
  disableDoeEmailField: boolean;
  showConfirmation: boolean;
  gafeEmailConflictErrMssg: string;
  doeEmailConflictErrMssg: string;
  createOrUpdateUserForm: ICreateOrUpdateUserForm;
  requireEmail: boolean;

  constructor(
    private $mdDialog,
    private ErrorModal,
    private school: IManageUsersSchool,
    private modalUser: IModalUserCreateOrUpdate,
    private schoolUsers,
    private MODE: TValidModalUserCreateOrUpdateModes,
    private User,
    private utilitiesService: UtilitiesService,
    private Rollbar,
    private store: Store<any>,
    private sdcActions: SdcActionsService,
  ) {
    this.JobRolesOptions = JobRolesOptions;
    this.doeEmployee = true; // default for 'Create a New User' is DOE employee question modal
    this.createDoeEmailSelected = false; // used for 'Create a New User' is DOE employee question modal
    this.disableDoeEmailField = this.MODE === 'UPDATE' && !this.modalUser.isSchoolTrustedUser ? true : false;
    this.showConfirmation = false; // used for 'Create a New User' show 'confirmation trusted status'
    this.requireEmail = false;
  }

  $onInit() {
    //
  }

  closeModal() {
    this.$mdDialog.hide();
  }

  requireDoeOrGafe(emailsEntered: { doeEmail: string; googleEmail: string }) {
    if (this.MODE === 'UPDATE') {
      const { doeEmail, googleEmail } = emailsEntered;
      if (!doeEmail && !googleEmail) {
        this.requireEmail = true;
      } else {
        this.requireEmail = false;
      }
    }
  }

  takeActionOnUser(modalUser: IModalUserCreateOrUpdate, createOrUpdateUserForm: ICreateOrUpdateUserForm) {
    // attach form to ctrl so that it can be used in #updateUser and #createUser
    this.createOrUpdateUserForm = createOrUpdateUserForm;

    if (this.MODE === 'UPDATE') {
      return this.updateUser(modalUser);
    } else {
      // before creating user, show 'confirm trusted status'
      this.showConfirmation = true;
    }
  }

  createNewUser(modalUser: IModalUserCreateOrUpdate) {
    const userWithTemplate = this.getNewUserTemplate(modalUser);
    const userToCreate = new this.User(userWithTemplate);

    userToCreate
      .$create()
      .then(async createdUser => {
        await this.syncState(createdUser);
        this.$mdDialog.hide({
          createdUser,
        });
      })
      .catch(error => {
        this.showConfirmation = false; // making this property false shows the form again

        return this.handleApiErr(error);
      });
  }

  updateUser(modalUser: IModalUserCreateOrUpdate) {
    const {
      _id,
      firstName: updatedFirstName,
      lastName: updatedLastName,
      jobRole: updatedJobRole,
      accessLevel,
    } = modalUser;

    // Need to convert a doeEmail value of `` to null
    const updatedDoeEmail = this.formatEmail(modalUser.doeEmail);
    const updatedGafeEmail = this.formatEmail(modalUser.googleEmail);

    const updatedDelegatedRole = this.getDelegatedRoleBasedOnAccessLevel(accessLevel);

    const paths = {
      firstName: 'preferredName.firstName',
      lastName: 'preferredName.lastName',
      delegatedRole: 'delegatedRole',
      gafeEmail: 'gafeEmail',
      jobRole: 'jobRole',
      doeEmail: 'doeEmail',
    };

    const userToUpdate: any = cloneDeep(find(this.schoolUsers, { _id })); // do not mutate schoolUsers
    const prevFirstName: string = this.utilitiesService.getFieldByPath(userToUpdate, paths.firstName);
    const prevLastName: string = this.utilitiesService.getFieldByPath(userToUpdate, paths.lastName);
    const prevDelegatedRole: string = this.utilitiesService.getFieldByPath(userToUpdate, paths.delegatedRole);
    const prevGafeEmail: string = this.utilitiesService.getFieldByPath(userToUpdate, paths.gafeEmail);
    const prevJobRole: string[] = this.utilitiesService.getFieldByPath(userToUpdate, paths.jobRole);
    const prevDoeEmail: string = this.utilitiesService.getFieldByPath(userToUpdate, paths.doeEmail);
    const fieldsToPatch: string[] = [];

    if (prevFirstName !== updatedFirstName) {
      const { firstName } = paths;

      this.utilitiesService.setFieldByPath(userToUpdate, firstName, updatedFirstName);
      fieldsToPatch.push(firstName);
    }

    if (prevLastName !== updatedLastName) {
      const { lastName } = paths;

      this.utilitiesService.setFieldByPath(userToUpdate, lastName, updatedLastName);
      fieldsToPatch.push(lastName);
    }

    if (prevDelegatedRole !== updatedDelegatedRole) {
      const { delegatedRole } = paths;

      this.utilitiesService.setFieldByPath(userToUpdate, delegatedRole, updatedDelegatedRole);
      fieldsToPatch.push(delegatedRole);
    }

    if (prevGafeEmail !== updatedGafeEmail) {
      const { gafeEmail } = paths;

      this.utilitiesService.setFieldByPath(userToUpdate, gafeEmail, updatedGafeEmail);
      fieldsToPatch.push(gafeEmail);
    }

    if (updatedJobRole && prevJobRole[0] !== updatedJobRole) {
      const { jobRole } = paths;

      this.utilitiesService.setFieldByPath(userToUpdate, jobRole, [updatedJobRole]);
      fieldsToPatch.push(jobRole);
    }

    if (prevDoeEmail !== updatedDoeEmail && modalUser.isSchoolTrustedUser) {
      const { doeEmail } = paths;

      this.utilitiesService.setFieldByPath(userToUpdate, doeEmail, updatedDoeEmail);
      fieldsToPatch.push(doeEmail);
    }

    const updatesExist = !!fieldsToPatch.length;

    if (updatesExist) {
      userToUpdate
        .$patchFields(fieldsToPatch)
        .then(async updatedSchoolUser => {
          await this.syncState(updatedSchoolUser);
          this.$mdDialog.hide({
            updatedSchoolUser: new this.User(updatedSchoolUser), // return instance of a user
          });
        })
        .catch(this.handleApiErr.bind(this));
    }
  }

  clearConflictErrMssgs(createOrUpdateUserForm: ICreateOrUpdateUserForm) {
    // reset conflict error
    if (createOrUpdateUserForm.googleEmail) delete createOrUpdateUserForm.googleEmail.$error.conflict;
    if (createOrUpdateUserForm.doeEmail) delete createOrUpdateUserForm.doeEmail.$error.conflict;

    this.gafeEmailConflictErrMssg = null;
    this.doeEmailConflictErrMssg = null;
  }

  doesFormHaveErr(createOrUpdateUserForm: ICreateOrUpdateUserForm) {
    return size(createOrUpdateUserForm.$error) > 0;
  }

  private getDelegatedRoleBasedOnAccessLevel(accessLevel): string {
    let newStandardizedDelegatedRole = UserAccessLevelToRoleTypeNoSchoolAdminMapping[accessLevel];
    newStandardizedDelegatedRole = newStandardizedDelegatedRole === 'school' ? null : newStandardizedDelegatedRole;

    return newStandardizedDelegatedRole;
  }

  private handleApiErr(error) {
    const { data } = error;

    if (data) {
      const { humanMessage, message, statusCode } = data;
      const messageToUse = (humanMessage || message).toLowerCase();

      if (statusCode === 409) {
        const conflictInGafeField = messageToUse.includes('google email');
        const conflictInDoeField = messageToUse.includes('doe email');

        if (conflictInGafeField) {
          this.gafeEmailConflictErrMssg = message;
          this.createOrUpdateUserForm.googleEmail.$error = { conflict: true }; // force error mssg to show in html
        } else if (conflictInDoeField) {
          this.doeEmailConflictErrMssg = message;
          this.createOrUpdateUserForm.doeEmail.$error = { conflict: true }; // force error mssg to show in html
        } else {
          this.Rollbar.error(`Manage User 409 API Error Not Handled Correctly. Update Code.`, error);
        }
      } else {
        this.ErrorModal.showNoRollbar('Error Creating/Updating User ', error);
      }
    } else {
      this.ErrorModal.showNoRollbar('Error Creating/Updating User ', error);
    }
  }

  private getNewUserTemplate(modalUser: IModalUserCreateOrUpdate) {
    const _id = this.utilitiesService.createV4Uuid();
    const firstName = modalUser.firstName;
    const lastName = modalUser.lastName;
    const doeEmail = this.formatEmail(modalUser.doeEmail);
    // modalUser static schema on backend does not allow for doeEmail to be part of sourceMetaData,
    // so we only pass it in as authenticationEmail
    const authenticationEmail = doeEmail || modalUser.googleEmail;
    const jobRole = modalUser.jobRole;
    const schoolId = this.school.schoolId;
    const gafeEmail = this.formatEmail(modalUser.googleEmail);
    const schoolType = this.school.schoolType;
    let delegatedRole = UserAccessLevelToRoleTypeNoSchoolAdminMapping[modalUser.accessLevel];

    if (delegatedRole === 'school') {
      delegatedRole = null;
    }

    const sourceMetaDataTemplate = this.getNewUserSourceMetaDataTemplate({ schoolType, doeEmail });
    const template: INewSchoolUserTemplate = {
      _id,
      name: {
        firstName,
        lastName,
      },
      authorizationStatus: 'FULL_ACCESS',
      authenticationEmail,
      gafeEmail,
      userType: 'NYC_SCHOOL',
      nvRole: {
        type: 'school',
        schoolId,
      },
      title: null,
      delegatedRole,
      preferredName: {
        firstName,
        lastName,
      },
      isSchoolTrustedUser: true,
      doeEmail,
      sourceMetaData: sourceMetaDataTemplate,
      isInSource: false,
    };

    if (jobRole) {
      template.jobRole = [jobRole];
    }

    return template;
  }

  private getNewUserSourceMetaDataTemplate({ schoolType, doeEmail }) {
    if (this.doeEmployee) {
      const sourceMetaData: ISchoolUserSourceMetaData = {
        employeeId: null,
        assignmentDescription: null,
        nvGafeEmail: null,
        schoolType,
        doeEmail,
        title: null,
        authorizationStatus: 'FULL_ACCESS',
      };

      return sourceMetaData;
    } else {
      const sourceMetaData: INycDoeCentralUserSourceMetaData = {
        nvGafeEmail: null,
        doeEmail: null,
        title: null,
        authorizationStatus: 'FULL_ACCESS',
      };

      return sourceMetaData;
    }
  }

  // used to trim and make empty strings into null values since the back end
  // does not allow empty strings as email values
  private formatEmail(email: string) {
    if (email) {
      email = email.trim();
      email = email.toLowerCase();
    }
    return email || null;
  }

  // TODO: once all actions for Users exist in store, use that infrastructure
  // to keep states in sync (CM).
  async syncState(user) {
    const users = await this.User.getAllSchoolUsers(user.nvRole.schoolId);
    const usersForSdcStore = this.User.getMinisForSdcStore(users);

    this.sdcActions.setAllSchoolUsers(usersForSdcStore);
    this.store.dispatch(new LoadUsersSuccess(users));
  }
}
